<template>
  <div
    class="ui-progress tw-w-full tw-overflow-hidden tw-bg-gray-200 tw-rounded"
    :style="{ height }"
  >
    <div
      :class="barClasses"
      :style="{ width: `${pct}%` }"
      role="progressbar"
      :aria-valuenow="value"
      aria-valuemin="0"
      :aria-valuemax="max"
    >
      <span v-if="label" class="tw-text-xs tw-font-semibold tw-text-white tw-px-2">{{ label }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  /**
   * UiProgress — Tailwind primitive replacing BootstrapVue-Next's
   * `b-progress` + `b-progress-bar` pair.
   *
   * The codebase only uses single-bar progress meters, so the API is
   * flat — no nested <UiProgressBar/>. Pass `value`, `max`, `variant`,
   * etc. directly. The component renders the gray track + filled bar in
   * one shot and is sized via the `height` prop (any CSS value).
   *
   * `animated` implies `striped` because Bootstrap's animation moves the
   * stripe pattern; without stripes there's nothing to animate. Both
   * patterns and the keyframes live in scoped CSS rather than Tailwind
   * utilities because the project doesn't have a striped-bg utility and
   * one-off custom CSS reads more clearly than @apply churn.
   */
  import { computed } from 'vue'

  defineOptions({ name: 'UiProgress' })

  interface Props {
    value: number
    max?: number
    variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info'
    animated?: boolean
    striped?: boolean
    label?: string
    height?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    max: 100,
    variant: 'primary',
    animated: false,
    striped: false,
    label: '',
    height: '1rem',
  })

  const VARIANTS: Record<NonNullable<Props['variant']>, string> = {
    primary:   'tw-bg-blue-600',
    secondary: 'tw-bg-gray-500',
    success:   'tw-bg-green-600',
    danger:    'tw-bg-red-600',
    warning:   'tw-bg-yellow-400',
    info:      'tw-bg-cyan-500',
  }

  const pct = computed(() => {
    if (!props.max) return 0
    return Math.max(0, Math.min(100, (props.value / props.max) * 100))
  })

  const barClasses = computed(() => [
    'tw-h-full tw-flex tw-items-center tw-justify-center tw-text-white tw-transition-[width] tw-duration-500',
    VARIANTS[props.variant],
    (props.striped || props.animated) && 'ui-progress-striped',
    props.animated && 'ui-progress-animated',
  ])
</script>

<style scoped>
.ui-progress-striped {
  background-image: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.15) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.15) 75%,
    transparent 75%,
    transparent
  );
  background-size: 1rem 1rem;
}
.ui-progress-animated {
  animation: ui-progress-stripes 1s linear infinite;
}
@keyframes ui-progress-stripes {
  from { background-position: 1rem 0; }
  to   { background-position: 0 0; }
}
</style>
